import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// API's
import { recalculateWashout } from "@/Washouts/api/common/mutations";
// Components
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import SalesManagerWashoutContent from "../../Sales/Managers/SalesManagerWashoutContent";
import UpdatedOn from "@/Washouts/components/UpdatedOn";
// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";
// Constants
import actionsList from "@/Constants/actionsList";
// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
// Helpers
import { handleWashoutSubscriptions } from "@/Washouts/helpers/washoutSubscriptions";
import useApproveRetractWashoutSubscription from "@/Washouts/hooks/WashoutReview/useApproveRetractWashoutSubscription";
import useApproveAllWashoutSubscription from "@/Washouts/hooks/common/useApproveAllWashoutSubscription";

const positionLabel = "Finance Director";

export default function FinanceDirectorDepartmentalWashoutContainer(props) {
	const queryClient = useQueryClient();
	const { useWashoutQueries } = useFactoryWashout();
	const washoutQueries = useWashoutQueries(positionLabel, "Departmental");

	const selected = React.useRef(null);
	const { getSubscription } = useSubscriptions();

	const subscriptionsInformation = props?.subscriptionsInformation;

	const { data: model, isLoading } = useQuery({
		queryKey: [
			"getWashout",
			props.selected?.payPeriodId,
			props.selected?.position,
			props.selected?.userId,
			props.selected?.commissionCycleId,
		],
		queryFn: async () => {
			const { locationId } = props.location || {};
			const { userId, commissionCycleId, payPeriodId, position } = props.selected || {};
			return washoutQueries?.getWashout(locationId, userId, commissionCycleId, payPeriodId, position);
		},
		enabled: !!props.location && !!props.selected,
		staleTime: Infinity,
	});

	const { mutate: recalculate } = useMutation({
		mutationKey: ["recalculateWashout"],
		mutationFn: (params) => recalculateWashout(params),
	});

	useApproveRetractWashoutSubscription({
		locationId: props.location?.locationId,
		commissionCycleId: props.selected?.commissionCycleId,
		payPeriodId: props.selected?.payPeriodId,
		updateWashoutModel: true,
	});

	useApproveAllWashoutSubscription({
		locationId: props.location?.locationId,
		commissionCycleId: props.selected?.commissionCycleId,
		payPeriodId: props.selected?.payPeriodId,
	});

	const handleSummaryChange = (input) => {
		if (input && props.location) {
			const params = {
				input,
				position: model?.employee?.position || positionLabel,
				isDepartmental: true,
				locationId: props.location.locationId,
				washoutId: model._id,
			};
			recalculate(params);
		}
	};

	React.useEffect(() => {
		selected.current = props.selected;
		handleWashoutSubscriptions({
			subscriptionsInformation,
			props: {
				...props,
				positionWithoutSpaces: "FinanceDirectorDepartmental",
			},
			actions: {
				queryClient,
				getSubscription,
			},
			options: {
				useRanking: false,
			},
		});

		return () => {
			selected.current = props.selected;
		};
	}, [props, model, getSubscription, queryClient]);

	const { useAuth } = useAuthorization();
	const overrideCompensation = useAuth(actionsList.OVERRIDE_FINANCE_DIRECTOR_COMPENSATION);

	return (
		<React.Fragment>
			<SalesManagerWashoutContent
				model={model}
				locationId={props.location.locationId}
				isLoading={isLoading}
				overrideCompensation={overrideCompensation}
				editable={true}
				selected={props.selected}
				handleSummaryChange={handleSummaryChange}
			/>

			{!model && !isLoading && (
				<RoundedBox>
					<EmptyBody
						image={props.selected ? emptyTable : empty}
						title={props.selected ? "No washout found for this director" : "Select a Finance Director to view their washout"}
						bodyHeight={"510px"}
						sx={{
							boxShadow: "none !important",
						}}
					/>
				</RoundedBox>
			)}

			<UpdatedOn isLoading={isLoading} updatedOn={model?.created} />
		</React.Fragment>
	);
}

import PropTypes from "prop-types";
FinanceDirectorDepartmentalWashoutContainer.propTypes = {
	model: PropTypes.shape({
		productConversions: PropTypes.array,
		callActivityPerformance: PropTypes.array,
	}),
	location: PropTypes.shape({
		locationId: PropTypes.string,
	}),
	selected: PropTypes.shape({
		userId: PropTypes.string,
		commissionCycleId: PropTypes.string,
		payPeriodId: PropTypes.string,
		status: PropTypes.string,
		position: PropTypes.string,
	}),
	subscriptionsInformation: PropTypes.object,
};
